/**
 * ⓘ card for the E2 Sankey: the common devices behind each end-use bucket
 * this site's Sankey shows, in the Sankey's own order (largest $ first).
 */
import type React from "react"
import { useTranslation } from "react-i18next"

import { Box, Stack, Typography } from "@mui/material"

import { ENDUSE_SOURCE_KEYS, normalizeEnduse } from "./e2-info-content"
import { InfoPopover } from "./info-popover"
import type { E2Driver } from "./schedule-misalignment.types"

export const EnduseSourcesInfo: React.FC<{ drivers: E2Driver[] }> = ({
  drivers,
}) => {
  const { t } = useTranslation("common")
  const buckets = [...drivers]
    .sort((a, b) => b.impact_dollars - a.impact_dollars)
    .flatMap((d) => {
      const enduse = normalizeEnduse(d.enduse)
      const key = ENDUSE_SOURCE_KEYS[enduse]
      return key ? [key] : []
    })

  return (
    <InfoPopover
      label={t("neat.e2.info.enduseSources.open")}
      title={t("neat.e2.info.enduseSources.title")}
    >
      <Typography variant="body2">
        {t("neat.e2.info.enduseSources.intro")}
      </Typography>
      {buckets.length > 0 ? (
        <Stack component="dl" spacing={0.75} sx={{ m: 0 }}>
          {buckets.map((key) => (
            <Box key={key}>
              <Typography
                component="dt"
                sx={{ fontWeight: "medium" }}
                variant="body2"
              >
                {t(`neat.e2.info.enduseSources.names.${key}`)}
              </Typography>
              <Typography
                component="dd"
                sx={{ color: "text.secondary", m: 0 }}
                variant="body2"
              >
                {t(`neat.e2.info.enduseSources.sources.${key}`)}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : null}
      <Typography sx={{ color: "text.secondary" }} variant="caption">
        {t("neat.e2.info.enduseSources.footer")}
      </Typography>
    </InfoPopover>
  )
}
